/**
 * Region Configuration
 *
 * This module provides region-specific values based on the VITE_REGION environment variable.
 * Values are determined at build time for each Netlify deployment.
 *
 * Domains additionally depend on VITE_DOMAIN_CUTOVER (the single-brand domain migration):
 *   Before cutover (flag unset):  US build → storily.io,    UK build → storymarketing.uk
 *   After cutover  (flag "true"): UK build → storily.io,    US build → us.storily.io
 * See docs/uk-us-domain-migration.md for the launch-day runbook.
 */

type Region = 'US' | 'UK';

const region: Region = (import.meta.env.VITE_REGION as Region) || 'US';

export const isUK = region === 'UK';
export const isUS = region === 'US';

/**
 * Launch-day switch, set per-site in the Netlify dashboard (exactly "true").
 * Until then both deployments keep their current domains and branding.
 */
export const domainCutover = import.meta.env.VITE_DOMAIN_CUTOVER === 'true';

const BASE_URLS: Record<'pre' | 'post', Record<Region, string>> = {
  pre: { US: 'https://storily.io', UK: 'https://storymarketing.uk' },
  post: { US: 'https://us.storily.io', UK: 'https://storily.io' },
};

const baseUrls = domainCutover ? BASE_URLS.post : BASE_URLS.pre;

/** The other region's site — used for hreflang pairs and the region-suggestion banner */
export const alternateRegion: Region = isUK ? 'US' : 'UK';
export const alternateBaseUrl = baseUrls[alternateRegion];

/** hreflang x-default: rest-of-world visitors get the US site ($ pricing travels best) */
export const X_DEFAULT_BASE_URL = BASE_URLS.post.US;

export const regionConfig = {
  region,

  // Currency
  currency: isUK ? '£' : '$',

  // Contact Information
  email: 'hello@storymarketing.group', // Same for both regions

  // Address
  address: isUK
    ? 'The Kiln, 2 Copenhagen St, Worcester WR1 2HB'
    : '800 Town & Country Blvd Suite 500, Houston, TX 77024',
  countryRegion: isUK ? 'Worcester, United Kingdom' : 'Texas, United States',
  mapsUrl: isUK
    ? 'https://www.google.com/maps/place/The+Kiln,+2+Copenhagen+St,+Worcester+WR1+2HB,+UK'
    : 'https://www.google.com/maps/place/800+Town+and+Country+Blvd+Suite+500,+Houston,+TX+77024,+USA',

  // Branding — unified under "Storily" once the domain cutover happens
  siteName: isUK && !domainCutover ? 'Story Marketing' : 'Storily',
  baseUrl: baseUrls[region],
  
  // Legal/Compliance (UK-specific)
  icoRegistration: isUK ? 'ZC060558' : null,
  dpo: isUK ? {
    name: 'Kyle Jones',
    email: 'kyle@storymarketing.group',
    address: 'The Kiln, 2 Copenhagen St, Worcester WR1 2HB'
  } : null,
  governingLaw: isUK ? 'England and Wales' : 'United States',
  lawfulBases: isUK ? ['consent', 'contract', 'legitimate interests'] : [],
  
  // Pricing (same values, different currency display)
  pricing: {
    minimums: 197,
    professional: 297,
    enterprise: 697,
    minimumsAnnual: 1970,
    professionalAnnual: 2970,
    enterpriseAnnual: 6970,
  },
  
  // Credits (formatted with currency)
  credits: {
    minimums: isUK ? '£10' : '$10',
    professional: isUK ? '£25' : '$25',
    enterprise: isUK ? '£100' : '$100',
  },
  
  // Savings display
  savings: {
    monthly: isUK ? '£1,968' : '$1,968',
    competitorTotal: isUK ? '£2,165' : '$2,165',
    storilyPrice: isUK ? '£197' : '$197',
  }
} as const;

/**
 * Format a price with the region's currency symbol
 */
export const formatPrice = (amount: number): string => {
  return `${regionConfig.currency}${amount}`;
};

/**
 * Format a price per month
 */
export const formatPricePerMonth = (amount: number): string => {
  return `${regionConfig.currency}${amount}/mo`;
};

/**
 * Localize any string containing "$" to the region's currency symbol.
 * No-op for US builds.
 */
export const localizeCurrency = (text: string): string => {
  if (!isUK) return text;
  return text.replace(/\$/g, '£');
};

/** Short alias */
export const c = localizeCurrency;

export default regionConfig;
